import { gql } from '@apollo/client';
import { MODEL_CHOICE, OPTIMAL_PLAN } from '../mutations/goals';

/**
 * Running the analysis on a goal.
 *
 * THE PLAN COMES FROM HERE, not from the create. `create<Type>Goal` records the
 * goal and answers with `investmentplan: null` on every type. Its response type
 * has an `optimalPlan` field, which is what made the create look like it
 * planned; the field is simply never filled.
 *
 * The planner is a SUBSCRIPTION per goal type, taking that type's `*Update`
 * input and streaming an `InvestmentPlanResponse`: the optimal plan, the model
 * it recommends with every variant's instruments, the alternatives it weighed,
 * and the AI summary. One message, then the stream idles.
 *
 * It runs from the answers alone, BEFORE any goal exists — the order the app's
 * goal flows use:
 *   plan<Type>            → what committing to it would look like
 *   create<Type>Goal      → the goal exists, once the advisor commits
 *   finalize<Type>Plan    → the plan is written and the goal committed
 * So an advisor who looks at a plan and walks away leaves no goal behind.
 *
 * CLIENT SCOPE: a subscription travels over the websocket, whose connection
 * params carry only the token, and the backend reads `X-Target-User-Id` from
 * HTTP headers alone. The analysis therefore runs as the signed-in advisor —
 * any profile enrichment the planner does uses theirs — until the backend
 * accepts the target user on the socket.
 */
const PLAN = `
  investmentplanId
  status
  targetValue
  monthlyContribution
  yearlyContribution
  numberOfMonthsForPlan
  plannedTotalAssets
  plannedFundedStatus
  targetInvestmentRisk
  description { status summary contributions investmentGains actionItems progress recommendations }
  aiSummary { resultFlag summary recommendations }
  ${OPTIMAL_PLAN}
  ${MODEL_CHOICE}
`;

export const PLAN_EDUCATION = gql`
  subscription PlanEducation($input: EducationUpdate!) {
    planEducation(input: $input) { ${PLAN} }
  }
`;

export const PLAN_RETIREMENT = gql`
  subscription PlanRetirement($input: RetirementUpdate!) {
    planRetirement(input: $input) { ${PLAN} }
  }
`;

export const PLAN_TRAVEL = gql`
  subscription PlanTravelGoal($input: TravelUpdate!) {
    planTravelGoal(input: $input) { ${PLAN} }
  }
`;

export const PLAN_BUY_HOME = gql`
  subscription PlanBuyhome($input: BuyHomeUpdate!) {
    planBuyhome(input: $input) { ${PLAN} }
  }
`;

export const PLAN_BUY_VEHICLE = gql`
  subscription PlanBuyvehicle($input: BuyVehicleUpdate!) {
    planBuyvehicle(input: $input) { ${PLAN} }
  }
`;

export const PLAN_EMERGENCY_FUND = gql`
  subscription PlanEmergencyFund($input: EmergencyFundUpdate!) {
    planEmergencyFund(input: $input) { ${PLAN} }
  }
`;

export const PLAN_KIDS_MARRIAGE = gql`
  subscription PlanKidsMarriage($input: KidsMarriageUpdate!) {
    planKidsMarriage(input: $input) { ${PLAN} }
  }
`;

export const PLAN_DEBT_PAYDOWN = gql`
  subscription PlanDebtPaydown($input: DebtPaydownUpdate!) {
    planDebtPaydown(input: $input) { ${PLAN} }
  }
`;

export const PLAN_CAPITAL_TARGET = gql`
  subscription PlanCapitalTarget($input: CapitalTargetUpdate!) {
    planCapitalTarget(input: $input) { ${PLAN} }
  }
`;

export const PLAN_LONG_TERM_SAVINGS = gql`
  subscription PlanLongTermSavings($input: LongTermSavingsUpdate!) {
    planLongTermSavings(input: $input) { ${PLAN} }
  }
`;

export const PLAN_GOLD_SAVINGS = gql`
  subscription PlanGoldSavings($input: GoldSavingsUpdate!) {
    planGoldSavings(input: $input) { ${PLAN} }
  }
`;
